/*============================================================================
Quantity Break Price Per Unit Calculator (v1.1.0)
==============================================================================*/

(function() {
  // Configuration
  const CONFIG = {
    version: '1.1.0',
    debug: false, // Debug flag
    selectors: {
      quantityBreakWidget: '.bogos-bundles-quantity-break-widget',
      subTitle: '.bogos-bundle-quantity-break-sub-title',
      quantityTitle: '.bogos-bundle-quantity-break-title',
      discountPrice: '.bogos-bundle-quantity-break-item-discount-price'
    },
    retryConfig: {
      maxRetries: 10,
      retryInterval: 500, // milliseconds
    }
  };

  // Debug logger
  const logger = {
    log(...args) {
      if (CONFIG.debug) {
        console.log('🔍 [Price Calculator]:', ...args);
      }
    },
    warn(...args) {
      if (CONFIG.debug) {
        console.warn('⚠️ [Price Calculator]:', ...args);
      }
    },
    error(...args) {
      if (CONFIG.debug) {
        console.error('❌ [Price Calculator]:', ...args);
      }
    },
    success(...args) {
      if (CONFIG.debug) {
        console.log('✅ [Price Calculator]:', ...args);
      }
    }
  };

  // Log version on script load
  logger.log(`Initializing version ${CONFIG.version}`);

  // Utility functions
  const utils = {
    getMarketHandle() {
      const marketMeta = document.querySelector('meta[name="market-handle"]');
      const handle = marketMeta ? marketMeta.getAttribute('content') : null;
      logger.log('Market Handle:', handle);
      return handle;
    },

    extractQuantity(titleText) {
      const match = titleText.match(/(\d+)/);
      const quantity = match ? parseInt(match[1], 10) : 0;
      logger.log('Extracted Quantity:', quantity, 'from:', titleText);
      return quantity;
    },

    extractPrice(priceText) {
      // Remove currency symbol and any whitespace, replace comma with dot
      const price = priceText.replace(/[^0-9,]/g, '').replace(',', '.');
      const parsedPrice = parseFloat(price);
      logger.log('Extracted Price:', parsedPrice, 'from:', priceText);
      return parsedPrice;
    },

    formatPrice(price) {
      // Format with German number format (comma as decimal separator)
      const formatted = `${price.toFixed(2).replace('.', ',')} € pro Stück`;
      logger.log('Formatted Price:', formatted);
      return formatted;
    },

    // Wait for element to be available
    waitForElement(selector, timeout = 10000) {
      return new Promise((resolve) => {
        if (document.querySelector(selector)) {
          return resolve(document.querySelector(selector));
        }

        const observer = new MutationObserver(() => {
          if (document.querySelector(selector)) {
            observer.disconnect();
            resolve(document.querySelector(selector));
          }
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true
        });

        // Set timeout
        setTimeout(() => {
          observer.disconnect();
          resolve(null);
        }, timeout);
      });
    }
  };

  // Price Calculator
  const priceCalculator = {
    calculatePricePerUnit(totalPrice, quantity) {
      const pricePerUnit = totalPrice / quantity;
      logger.log('Calculated Price per Unit:', pricePerUnit, '(', totalPrice, '/', quantity, ')');
      return pricePerUnit;
    },

    updatePricePerUnit(itemContainer) {
      logger.log('Updating price per unit for container:', itemContainer);

      // Get the quantity from the title
      const titleElement = itemContainer.querySelector(CONFIG.selectors.quantityTitle);
      if (!titleElement) {
        logger.error('Title element not found');
        return;
      }
      const quantity = utils.extractQuantity(titleElement.textContent);

      // Get the total price from the discount price element
      const priceElement = itemContainer.querySelector(CONFIG.selectors.discountPrice);
      if (!priceElement) {
        logger.error('Price element not found');
        return;
      }
      
      const totalPrice = utils.extractPrice(priceElement.textContent);

      if (quantity && totalPrice) {
        // Calculate price per unit
        const pricePerUnit = this.calculatePricePerUnit(totalPrice, quantity);
        
        // Update the subtitle text
        const subTitleElement = itemContainer.querySelector(CONFIG.selectors.subTitle);
        if (subTitleElement) {
          const oldText = subTitleElement.textContent;
          subTitleElement.textContent = utils.formatPrice(pricePerUnit);
          logger.success('Updated subtitle from:', oldText, 'to:', subTitleElement.textContent);
        } else {
          logger.error('Subtitle element not found');
        }
      } else {
        logger.warn('Invalid quantity or price:', { quantity, totalPrice });
      }
    }
  };

  // Main functionality
  async function initWithRetry(retryCount = 0) {
    logger.log(`Initializing Price Calculator (attempt ${retryCount + 1})`);
    const marketHandle = utils.getMarketHandle();
    
    if (marketHandle !== 'welt') {
      logger.log('Market is not "welt", skipping updates');
      return;
    }

    logger.log('Market is "welt", proceeding with updates');
    
    // Wait for the first widget to appear
    const widget = await utils.waitForElement(CONFIG.selectors.quantityBreakWidget, CONFIG.retryConfig.retryInterval);
    
    if (!widget) {
      if (retryCount < CONFIG.retryConfig.maxRetries) {
        logger.warn(`No widgets found, retrying in ${CONFIG.retryConfig.retryInterval}ms... (${retryCount + 1}/${CONFIG.retryConfig.maxRetries})`);
        setTimeout(() => initWithRetry(retryCount + 1), CONFIG.retryConfig.retryInterval);
        return;
      }
      logger.error('Max retries reached, no widgets found');
      return;
    }

    // Find all widgets now that we know they exist
    const widgets = document.querySelectorAll(CONFIG.selectors.quantityBreakWidget);
    logger.log('Found widgets:', widgets.length);
    
    widgets.forEach((widget, index) => {
      logger.log(`Processing widget ${index + 1} of ${widgets.length}`);
      const itemContainers = widget.querySelectorAll('.bogos-bundle-quantity-break_item-container');
      logger.log(`Found ${itemContainers.length} item containers in widget ${index + 1}`);
      
      itemContainers.forEach((container, containerIndex) => {
        logger.log(`Processing container ${containerIndex + 1} of ${itemContainers.length}`);
        priceCalculator.updatePricePerUnit(container);
      });
    });
    
    logger.success('Price Calculator initialization complete');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    logger.log('Document still loading, waiting for DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', () => initWithRetry());
  } else {
    logger.log('Document already loaded, initializing immediately');
    initWithRetry();
  }

  // Re-run on Shopify section updates
  document.addEventListener('shopify:section:load', () => {
    logger.log('Section updated, re-initializing');
    initWithRetry();
  });

  // Listen for BOGOS events
  document.addEventListener('BOGOS:loaded', () => {
    logger.log('BOGOS loaded event detected, re-initializing');
    initWithRetry();
  });

  // Also listen for any changes to the widgets
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'childList' && 
          (mutation.target.matches(CONFIG.selectors.quantityBreakWidget) || 
           mutation.target.querySelector(CONFIG.selectors.quantityBreakWidget))) {
        logger.log('Widget mutation detected, re-initializing');
        initWithRetry();
        break;
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
})(); 